import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";

/**
 * Open/close state and fixed positioning for a small popup menu anchored to a button.
 * The menu is positioned against the viewport, so it isn't clipped by scrolling table
 * containers, and it opens upwards when there isn't room below. Closes on outside
 * click, Escape, scroll and resize.
 */
export function useAnchoredMenu(estimatedHeight = 220) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<CSSProperties>({});
  const anchorRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const below = window.innerHeight - rect.bottom;
    const up = below < estimatedHeight + 12 && rect.top > below;
    setStyle({
      position: "fixed",
      right: Math.max(8, window.innerWidth - rect.right),
      ...(up ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      zIndex: 60,
    });
  }, [estimatedHeight]);

  const toggle = useCallback(() => {
    setOpen((o) => {
      if (!o) place();
      return !o;
    });
  }, [place]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        anchorRef.current?.focus();
      }
    };
    const close = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [open]);

  return { open, setOpen, toggle, anchorRef, menuRef, style };
}
